import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, vModelText as _vModelText, withDirectives as _withDirectives, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "inmail-modal__header-inner" }
const _hoisted_2 = {
  key: 0,
  class: "inmail-modal__subtitle"
}
const _hoisted_3 = { class: "inmail-modal__body" }
const _hoisted_4 = {
  key: 0,
  class: "inmail-modal__recipient"
}
const _hoisted_5 = { class: "inmail-modal__recipient-info" }
const _hoisted_6 = { class: "inmail-modal__recipient-avatar" }
const _hoisted_7 = { class: "inmail-modal__recipient-details" }
const _hoisted_8 = { class: "inmail-modal__recipient-name" }
const _hoisted_9 = { class: "inmail-modal__recipient-title" }
const _hoisted_10 = {
  key: 0,
  class: "inmail-modal__credits-badge"
}
const _hoisted_11 = { class: "inmail-modal__form-group" }
const _hoisted_12 = ["disabled"]
const _hoisted_13 = { class: "inmail-modal__form-group" }
const _hoisted_14 = { class: "inmail-modal__textarea-wrapper" }
const _hoisted_15 = ["disabled"]
const _hoisted_16 = { class: "inmail-modal__ai-row" }
const _hoisted_17 = { class: "inmail-modal__ai-buttons" }
const _hoisted_18 = ["disabled", "title"]
const _hoisted_19 = ["disabled", "title"]
const _hoisted_20 = {
  key: 1,
  class: "inmail-modal__error"
}
const _hoisted_21 = { class: "inmail-modal__actions" }

import { Icon } from "@iconify/vue";
import { ref, computed, watch } from "vue";
import { Button, Modal } from "@/components/ui";
import Unipile from "@/services/unipile/unipile";
import AiRefinePopover from "@/components/socials/AiRefinePopover.vue";
import AIGenerationService from "@/services/ai/ai-generation";
import { useToast } from "@/composables/ui/useToast";
import { useResolvedAiPrompts } from "@/composables/useAiPrompts";

interface Props {
  show: boolean;
  contact?: any;
  accountId?: string;
  providerId?: string;
  icpId?: string;
  initialSubject?: string;
  initialBody?: string;
  inmailBalance?: {
    premium?: number;
    recruiter?: number;
    sales_navigator?: number;
    activeSubscriptionType?: "sales_navigator" | "recruiter" | "classic" | null;
    activeCredits?: number;
  } | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'InMailCompositionModal',
  props: {
    show: { type: Boolean },
    contact: { default: null },
    accountId: { default: undefined },
    providerId: { default: undefined },
    icpId: { default: undefined },
    initialSubject: { default: undefined },
    initialBody: { default: undefined },
    inmailBalance: { default: null }
  },
  emits: ["close", "sent"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const $toast = useToast();

const emit = __emit;

const subjectText = ref("");
const messageText = ref("");
const isLoading = ref(false);
const error = ref<string | null>(null);
const showAiPanel = ref(false);
const showAiComposePanel = ref(false);
const isGeneratingAiCustom = ref(false);
const isGeneratingAiCompose = ref(false);
const aiRefineButtonRef = ref<HTMLElement | null>(null);
const aiComposeButtonRef = ref<HTMLElement | null>(null);
const resolvedAiPromptsQuery = useResolvedAiPrompts();

const toggleAiRefinePanel = () => {
  showAiComposePanel.value = false;
  showAiPanel.value = !showAiPanel.value;
};

const toggleAiComposePanel = () => {
  showAiPanel.value = false;
  showAiComposePanel.value = !showAiComposePanel.value;
};

const handleAiCustomizationGenerate = async (payload: {
  customInstructions: string;
  attachments: File[];
}) => {
  if (isGeneratingAiCustom.value) return;
  isGeneratingAiCustom.value = true;
  try {
    const response = await AIGenerationService.generateRefine({
      contactId: props.contact?.id,
      icpId: props.icpId,
      channel: "linkedin",
      currentDraft: messageText.value,
      customInstructions: payload.customInstructions,
      files: payload.attachments,
    });
    const text = response.body;
    messageText.value = text;
    showAiPanel.value = false;
    $toast.success("Message refined");
  } catch (err: any) {
    $toast.error(err?.response?.data?.message || "Failed to refine message");
  } finally {
    isGeneratingAiCustom.value = false;
  }
};

const handleAiComposeGenerate = async (payload: { customInstructions: string }) => {
  if (isGeneratingAiCompose.value) return;
  isGeneratingAiCompose.value = true;
  try {
    const response = await AIGenerationService.generateCompose({
      contactId: props.contact?.id,
      icpId: props.icpId,
      channel: "linkedin",
      customInstructions: payload.customInstructions,
      aiPromptCompose: resolvedAiPromptsQuery.composeAiPrompt.value,
    });
    messageText.value = response.body;
    showAiComposePanel.value = false;
    $toast.success("Message composed");
  } catch (err: any) {
    $toast.error(err?.response?.data?.message || "Failed to generate message");
  } finally {
    isGeneratingAiCompose.value = false;
  }
};

const contactName = computed(() => {
  if (!props.contact) return "";
  const fname = props.contact.fname || "";
  const lname = props.contact.lname || "";
  return `${fname} ${lname}`.trim() || "Unknown";
});

const contactTitle = computed(() => {
  return props.contact?.jobTitle || "";
});

const contactCompany = computed(() => {
  return props.contact?.company?.name || props.contact?.company || "";
});

const canSend = computed(() => {
  return subjectText.value.trim().length > 0 && props.accountId && props.providerId;
});

const getTotalCredits = (balance: any) => {
  if (!balance) return 0;
  if (balance.activeCredits !== undefined) {
    return balance.activeCredits;
  }
  return (balance.premium || 0) + (balance.recruiter || 0) + (balance.sales_navigator || 0);
};

watch(
  () => props.show,
  (isShowing) => {
    if (isShowing) {
      subjectText.value = props.initialSubject || "";
      messageText.value = props.initialBody || "";
      error.value = null;
      showAiPanel.value = false;
      showAiComposePanel.value = false;
    }
  }
);

const handleSend = async () => {
  if (!canSend.value || isLoading.value) return;

  error.value = null;
  isLoading.value = true;

  try {
    if (!props.accountId || !props.providerId) {
      throw new Error("Account ID and Provider ID are required");
    }
    // Pick LinkedIn API type based on subscription priority
    let linkedinApi: "classic" | "recruiter" | "sales_navigator" = "classic";
    if (props.inmailBalance?.activeSubscriptionType) {
      if (props.inmailBalance.activeSubscriptionType === "sales_navigator") {
        linkedinApi = "sales_navigator";
      } else if (props.inmailBalance.activeSubscriptionType === "recruiter") {
        linkedinApi = "recruiter";
      }
    } else if (props.inmailBalance) {
      if (props.inmailBalance.sales_navigator && props.inmailBalance.sales_navigator > 0) {
        linkedinApi = "sales_navigator";
      } else if (props.inmailBalance.recruiter && props.inmailBalance.recruiter > 0) {
        linkedinApi = "recruiter";
      }
    }

    const requestPayload: any = {
      account_id: props.accountId,
      attendees_ids: [props.providerId],
      subject: subjectText.value.trim(),
      text: messageText.value.trim() || undefined,
      linkedin: {
        inmail: true,
        api: linkedinApi,
      },
      contactId: props.contact?.id || undefined,
    };

    const response = await Unipile.createGroupChat(requestPayload);

    if (response?.data?.status && response?.data?.data) {
      emit("sent");
    } else {
      throw new Error("Failed to send InMail");
    }
  } catch (err: any) {
    console.error("Failed to send InMail:", err);
    error.value =
      err.response?.data?.message ||
      err.response?.data?.errorMessage ||
      err.message ||
      "Failed to send InMail. Please try again.";
  } finally {
    isLoading.value = false;
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Modal), {
    show: __props.show,
    title: "Send InMail",
    size: "md",
    "dialog-max-width": '560px',
    closeOnBackdrop: false,
    "backdrop-opacity": 0.3,
    onClose: _cache[5] || (_cache[5] = ($event: any) => (emit('close')))
  }, {
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _cache[6] || (_cache[6] = _createElementVNode("h2", {
          id: "modal-title",
          class: "inmail-modal__title"
        }, "Send InMail", -1)),
        (contactName.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_2, " Compose and send a direct message to " + _toDisplayString(contactName.value), 1))
          : _createCommentVNode("", true)
      ])
    ]),
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_21, [
        _createVNode(_unref(Button), {
          variant: "secondary",
          disabled: isLoading.value,
          onClick: _cache[4] || (_cache[4] = ($event: any) => (emit('close')))
        }, {
          default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
            _createTextVNode(" Cancel ", -1)
          ]))]),
          _: 1
        }, 8, ["disabled"]),
        _createVNode(_unref(Button), {
          variant: "primary",
          icon: "mdi:send",
          "icon-position": "left",
          loading: isLoading.value,
          disabled: isLoading.value || !canSend.value,
          onClick: handleSend
        }, {
          default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
            _createTextVNode(" Send ", -1)
          ]))]),
          _: 1
        }, 8, ["loading", "disabled"])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_3, [
        (__props.contact)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("div", _hoisted_6, [
                  _createVNode(_unref(Icon), { icon: "mdi:account-circle" })
                ]),
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("div", _hoisted_8, _toDisplayString(contactName.value), 1),
                  _createElementVNode("div", _hoisted_9, _toDisplayString(contactTitle.value) + " " + _toDisplayString(contactCompany.value ? `@ ${contactCompany.value}` : ""), 1)
                ])
              ]),
              (__props.inmailBalance)
                ? (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(getTotalCredits(__props.inmailBalance)) + " InMail credits remaining ", 1))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_11, [
          _cache[7] || (_cache[7] = _createElementVNode("label", {
            for: "subject",
            class: "inmail-modal__label"
          }, [
            _createTextVNode(" Subject "),
            _createElementVNode("span", { class: "inmail-modal__required" }, "(required)")
          ], -1)),
          _withDirectives(_createElementVNode("input", {
            id: "subject",
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((subjectText).value = $event)),
            type: "text",
            class: "inmail-modal__input",
            placeholder: "Enter subject",
            disabled: isLoading.value,
            required: ""
          }, null, 8, _hoisted_12), [
            [_vModelText, subjectText.value]
          ])
        ]),
        _createElementVNode("div", _hoisted_13, [
          _cache[10] || (_cache[10] = _createElementVNode("label", {
            for: "message",
            class: "inmail-modal__label"
          }, " Message ", -1)),
          _createElementVNode("div", _hoisted_14, [
            _withDirectives(_createElementVNode("textarea", {
              id: "message",
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((messageText).value = $event)),
              class: "inmail-modal__textarea",
              placeholder: "Type your message here",
              disabled: isLoading.value,
              rows: "8"
            }, null, 8, _hoisted_15), [
              [_vModelText, messageText.value]
            ])
          ]),
          _createElementVNode("div", _hoisted_16, [
            _createElementVNode("div", _hoisted_17, [
              _createElementVNode("button", {
                ref_key: "aiComposeButtonRef",
                ref: aiComposeButtonRef,
                type: "button",
                class: "inmail-modal__ai-btn inmail-modal__ai-btn--write",
                disabled: isGeneratingAiCompose.value,
                title: isGeneratingAiCompose.value ? 'Generating...' : 'AI Compose',
                onClick: toggleAiComposePanel
              }, [
                _createVNode(_unref(Icon), {
                  icon: "mdi:sparkles",
                  class: "inmail-modal__ai-btn-icon"
                }),
                _cache[8] || (_cache[8] = _createElementVNode("span", { class: "inmail-modal__ai-btn-text" }, "AI Compose", -1))
              ], 8, _hoisted_18),
              _createElementVNode("button", {
                ref_key: "aiRefineButtonRef",
                ref: aiRefineButtonRef,
                type: "button",
                class: "inmail-modal__ai-btn inmail-modal__ai-btn--refine",
                disabled: isGeneratingAiCustom.value,
                title: isGeneratingAiCustom.value ? 'Generating...' : 'AI Refine',
                onClick: toggleAiRefinePanel
              }, [
                _createVNode(_unref(Icon), {
                  icon: "mdi:sparkles",
                  class: "inmail-modal__ai-btn-icon"
                }),
                _cache[9] || (_cache[9] = _createElementVNode("span", { class: "inmail-modal__ai-btn-text" }, "AI Refine", -1))
              ], 8, _hoisted_19)
            ])
          ]),
          _createVNode(AiRefinePopover, {
            show: showAiComposePanel.value,
            "trigger-el": aiComposeButtonRef.value,
            disabled: isGeneratingAiCompose.value,
            title: "AI Compose",
            "generate-button-label": "Generate Message",
            "show-attachments": false,
            onGenerate: handleAiComposeGenerate,
            onClose: _cache[2] || (_cache[2] = ($event: any) => (showAiComposePanel.value = false))
          }, null, 8, ["show", "trigger-el", "disabled"]),
          _createVNode(AiRefinePopover, {
            show: showAiPanel.value,
            "trigger-el": aiRefineButtonRef.value,
            disabled: isGeneratingAiCustom.value,
            onGenerate: handleAiCustomizationGenerate,
            onClose: _cache[3] || (_cache[3] = ($event: any) => (showAiPanel.value = false))
          }, null, 8, ["show", "trigger-el", "disabled"])
        ]),
        (error.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
              _createVNode(_unref(Icon), {
                icon: "mdi:alert-circle",
                class: "inmail-modal__error-icon"
              }),
              _createElementVNode("span", null, _toDisplayString(error.value), 1)
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["show"]))
}
}

})